import { AxiosInstance } from "axios";
import * as tokenService from "./tokenService";
import { refreshSession } from "./refreshManager";
import { handleSessionExpired } from "./logout";

/**
 * Central 401 → refresh → retry interceptor.
 *
 * SHARED FILE — byte-identical in prx_shell-ui, prx_buyerapp-ui,
 * prx_buyer_WorkOrder_ui and prx_master-ui (canonical: prx_buyerapp-ui).
 * Only the attach call-site differs per app (rejectWith option).
 *
 * Contract preserved from the legacy interceptors:
 * - 409/400/404 RESOLVE with the response object (callers read res.data.status)
 * - rejectWith 'data'  → rejection value is error.response.data (what
 *   makeApiCall's catch expects); 'error' → the original AxiosError (plain
 *   axios try/catch callers: shell + the legacy /buyer instance)
 *
 * Retry policy (safe-retry, per the auth redesign): a request is retried ONLY
 * after a successful refresh triggered by its own 401 — a 401 is rejected at
 * the gateway before dispatch, so the retry cannot duplicate a transaction.
 * Network errors are NEVER blind-retried.
 */

const AUTH_ENDPOINTS = ["/auth/refresh", "/auth/login", "/auth/logout", "/auth/sso/login"];

// Backend AuthErrorFilter codes that mean a refresh cannot help.
const NON_REFRESHABLE_CODES = ["AUTH_INVALID", "SESSION_EXPIRED"];

export interface AttachAuthOptions {
  rejectWith?: "data" | "error";
}

export function attachAuthInterceptors(
  instance: AxiosInstance,
  options: AttachAuthOptions = {}
): AxiosInstance {
  const rejectWith = options.rejectWith || "data";
  const finalReject = (error: any) =>
    Promise.reject(rejectWith === "data" ? error?.response?.data ?? error : error);

  instance.interceptors.request.use((config) => {
    // Inject the raw cookie value (the gateway's expected Authorization
    // format) only when the caller didn't set its own header — makeApiCall
    // and makePublicApiCall keep doing that themselves.
    if (!config.headers?.Authorization) {
      const raw = tokenService.getAccessTokenRaw();
      if (raw) {
        config.headers = config.headers ?? ({} as any);
        config.headers.Authorization = raw;
      }
    }
    return config;
  });

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const original: any = error?.config ?? {};

      // No response at all (network failure, timeout): never blind-retry — a
      // mutation may have reached the server.
      if (!error?.response) {
        return Promise.reject(error);
      }

      const status = error.response.status;
      const url = String(original.url || "");
      const isAuthEndpoint = AUTH_ENDPOINTS.some((endpoint) => url.includes(endpoint));
      const isPublicQuotationPage = window.location.pathname.includes("/public-links/");
      const code = error.response.data?.code;

      if (
        status === 401 &&
        !isAuthEndpoint &&
        !isPublicQuotationPage &&
        !original._retry &&
        !NON_REFRESHABLE_CODES.includes(code)
      ) {
        original._retry = true;
        const refreshed = await refreshSession();
        if (refreshed) {
          const raw = tokenService.getAccessTokenRaw();
          if (raw) {
            original.headers = original.headers ?? {};
            original.headers.Authorization = raw;
          }
          return instance(original);
        }
        // Refresh token dead/revoked: graceful session-expired flow — no
        // hard redirect that would destroy form state.
        handleSessionExpired();
        return finalReject(error);
      }

      if (status === 409 || status === 400 || status === 404) {
        return error.response;
      }

      return finalReject(error);
    }
  );

  return instance;
}
